import { differenceInDays, isBefore } from "date-fns";
import format from "date-fns/format";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import React, { ComponentType, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, CalendarProps, Event, NavigateAction, View, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/sass/styles.scss";
import { useDrop } from "react-dnd";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { DEFAULT_CALENDAR_TASK_DURATION_MINS } from "@lifeat/shared/calendar/CalendarConstants";
import AbstractCalendarItem from "@lifeat/shared/calendar/impl/AbstractCalendarItem";
import { CalendarEvent } from "@lifeat/shared/calendar/impl/CalendarEvent";
import { CalendarRecurringTask } from "@lifeat/shared/calendar/impl/CalendarRecurringTask";
import CalendarTask from "@lifeat/shared/calendar/impl/CalendarTask";
import { CalendarItemInterface } from "@lifeat/shared/calendar/types/CalendarItemInterface";
import { CalendarList } from "@lifeat/shared/calendar/types/CalendarList";
import { CalendarDetails } from "@lifeat/shared/data-models/calendar/CalendarDetails";
import { Identifiable } from "@lifeat/shared/data-models/definition";
import { CalendarConnection } from "@lifeat/shared/data-models/user/integrations/IntegrationsDataEntity";
import {
    TASK_DURATION_MAXIMUM_MINUTES,
    TASK_DURATION_MINIMUM_MINUTES,
} from "@lifeat/shared/task-management/TaskManagementConstants";
import { isValidTaskDuration } from "@lifeat/shared/task-management/TaskManagementUtil";
import {
    MILLISECONDS_PER_DAY,
    calculateMinutesBetweenDates,
    calculateNumDaysBetweenTimestamps,
    formatMinutesToString,
    getDateTimestampDaysOnly,
    isDateInRange,
    millisecondsUntilNextWholeMinute,
    parseISODateStringForDate,
} from "@lifeat/shared/util/DateUtil";
import { Timeout } from "@lifeat/shared/util/TypeUtil";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import LifeAtErrorToast from "@common/LifeAtToast/LifeAtErrorToast";
import LifeAtToastContainer from "@common/LifeAtToast/LifeAtToastContainer";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import { useToastListContext } from "@contexts/ToastListContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { showMenubarWindow, updateTrayTitle } from "@desktop/ToDesktopService";
import useDatabaseState from "@hooks/useDatabaseState";
import { useEvent } from "@hooks/useEvent";
import useSessionStorage, { SessionStorageKeys } from "@hooks/useSessionStorage";
import { useComputed, useSignalEffect } from "@preact/signals-react";
import { logError } from "@service/ConsoleLoggerService";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { darkTheme, lightTheme } from "@themes/Theme";
import { buildNotification, clearCacheReloadPage } from "@util/BrowserUtil";
import { BrowserUtil } from "@util/index";
import { ClientTaskItemType } from "../../../features/task-management/TaskManagementTypes";
import { dateRangeStrategy, useRecurringTasks } from "../../../features/task-management/useRecurringTasks";
import { NotificationClickEvent } from "../../desktop-app/AmplitudeDesktopAppEvents";
import { ItemTypes } from "../../drag-and-drop/DragAndDropConstants";
import { projectList$, selectedProjectId$ } from "../../projects/ProjectGlobals";
import { AddTaskToCalendarEvent } from "../../task-management/AmplitudeTaskManagementEvents";
import { useTaskListContext } from "../../task-management/TaskListContext";
import { draggedTaskEvent$, selectedDetail$ } from "../../task-management/TaskManagementGlobals";
import { TaskManagementService } from "../../task-management/TaskManagementService";
import { getTaskTagFromTagArray } from "../../task-management/TaskManagementUtil";
import { mainContent$ } from "../../task-management/planner/PlannerGlobals";
import { EVENT_PAGE_SIZE_BY_DAYS } from "../CalendarConstants";
import {
    fetchEventsForDateRange,
    getProjectDeadlineEvents,
    getTaskEvents,
    joinEventMeetingLink,
} from "../CalendarService";
import "./CalendarDayView.scss";
import CalendarAllDayEventGutter from "./CalendarEvent/CalendarAllDayEventGutter";
import CalendarEventDetailView from "./CalendarEvent/CalendarEventDetailView";
import CalendarEventTitle from "./CalendarEvent/CalendarEventTitle";
import CalendarEventWrapper from "./CalendarEvent/CalendarEventWrapper";
import { CalendarSettingsModalHandle } from "./CalendarSettingsModal";
import CalendarTimeSlotWrapper from "./CalendarTimeSlotWrapper";
import CalendarToolbar, { CalendarToolbarPropsContext } from "./CalendarToolbar";
import CalendarWeekViewHeader from "./CalendarWeekViewHeader";

const locales = {
    "en-US": enUS,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

const CalendarDayViewWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    height: 100%;
    width: 100%;
`;

const StyledCalendarEventDetailView = styled(CalendarEventDetailView)<{ isVisible: boolean }>`
    margin-bottom: ${(props) => (props.isVisible ? 10 : 0)}px;
`;

const RbcCalendarWrapper = styled.div`
    border: none;
    border-radius: 10px;
    background-color: inherit;

    height: 100%;
    width: 100%;

    .rbc-calendar {
        height: 100%;
    }

    .rbc-time-content > * + * > *,
    .rbc-time-header-content,
    .rbc-time-header {
        border-left: none;
        border-right: none;
    }

    .rbc-allday-cell .rbc-row-content {
        max-height: 100px;
        overflow-y: scroll;
    }

    .rbc-time-view {
        border: 1px solid ${(props) => props.theme.borderPrimary};
        border-left: none;
        border-right: none;
    }

    .rbc-time-view .rbc-row {
        min-height: unset;
    }

    .rbc-timeslot-group {
        border-bottom: 1px solid ${(props) => props.theme.borderPrimary};
        min-height: 50px;
    }

    .rbc-time-slot {
        border-top: none;
    }

    .rbc-time-content {
        border-top: 1px solid ${(props) => props.theme.borderPrimary};
    }

    .rbc-current-time-indicator {
        background-color: ${(props) => props.theme.textColorPrimary};
        height: 2px;
    }
`;

const ErrorMessageContainer = styled.div`
    width: ${Styles.sidebarWidth};
    padding: 8px;
`;

const LegacyMigrationContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`;

const LegacyMigrationIcon = styled.span`
    font-size: 20px;
`;

const LegacyMigrationButton = styled(LifeAtButtonV2)`
    & .lifeat-button-content {
        padding: 5px 10px;
        font-size: ${FontSize.small};
        white-space: nowrap;
    }
`;

// TODO: integrate heartbeat (or on update with push token)
const FETCH_EVENTS_HEARTBEAT_MS = 60 * 1000; // 1 mins
const EVENT_COUNTDOWN_HEARTBEAT_MS = 60 * 1000; // 1 min

const NOTIFICATION_OFFSET = 0; // No offset for now, later will make this a setting
const TRAY_TEXT_LENGTH = 15;

const CALENDAR_EVENT_STEP = 15;
const CALENDAR_STEP_TIME_SLOTS = 4;

const UNTITLED_EVENT_TITLE = "Untitled event";

const RECURRING_DROP_ERROR_TITLE = "Failed to drag & drop";
const RECURRING_DROP_ERROR_DESCRIPTION = "Recurring task drag & drop is not supported yet.";

const COMPONENTS = {
    event: CalendarEventTitle,
    eventWrapper: CalendarEventWrapper,
    timeSlotWrapper: CalendarTimeSlotWrapper,
    timeGutterHeader: CalendarAllDayEventGutter,
    toolbar: CalendarToolbar,
    week: {
        header: CalendarWeekViewHeader,
    },
};

/**
 * DnD docs/examples:
 * https://jquense.github.io/react-big-calendar/examples/index.html?path=/docs/addons-drag-and-drop-introduction--page
 * https://github.com/jquense/react-big-calendar/blob/master/stories/demos/exampleCode/dndOutsideSource.js
 * https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/react-big-calendar/react-big-calendar-tests.tsx
 */
const DndCalendar = withDragAndDrop(Calendar as ComponentType<CalendarProps>);

interface CalendarViewProps {
    /**
     * Amplitude event source to indicate where the event was triggered from
     */
    readonly amplitudeEventSource: AmplitudeEventSource;
    /**
     * List of calendars to retrieve data for
     */
    readonly selectedCalendarList: CalendarDetails[];
    /**
     * List of calendar details
     */
    readonly calendars: CalendarList;
    readonly onSelectedCalendarChange: (calendar: CalendarDetails, selected: boolean) => void;
    readonly onCalendarConnectionRemove: (connection: CalendarConnection) => void;
    /**
     * If the only calendar being viewed is the user's Primary calendar.
     */
    readonly isOnlyPrimary: boolean;
    /**
     * Error message to display
     */
    readonly errorMsg: string | null;
    /**
     * How the calendar should display. By default is set to "day".
     */
    readonly calendarView?: "day" | "week";
    /**
     * If true, emit a JavaScript notification for the upcoming event.
     * By default, notifications will not be emitted.
     * This was added as a simple fix for preventing multiple of the same
     * notifications for being emitted when this component is rendered in
     * multiple places.
     */
    readonly sendNotifications?: boolean;
    readonly show?: boolean;
    className?: string;
}
export default function CalendarView(props: CalendarViewProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const history = useHistory();
    const databaseState = useDatabaseState();
    const { addToastNotification } = useToastListContext();

    const { user, userData }: UserContextType = useUserContext();
    const { lightMode } = useAppearanceContext();
    const theme = lightMode ? lightTheme : darkTheme;

    const settingsModalRef = useRef<CalendarSettingsModalHandle>(null);

    const todayDate = useMemo(() => new Date(), []);
    const [viewingDate, setViewingDate] = useState<Date>(todayDate);

    const [viewingEventId, setViewingEventId] = useState<string>(null);
    const [draggedEvent, setDraggedEvent] = useState<any>();

    const [calendarEvents, setCalendarEvents] = useState<{ [eventId: string]: CalendarEvent }>({});

    const notificationsTimeout = useRef([]); // used to keep track of active notification timeouts

    const [errorMsg, setErrorMsg] = useState<string>(undefined);
    const [hasSeenErrorMessage, setHasSeenErrorMessage] = useSessionStorage(
        SessionStorageKeys.CalendarEventErrorSeen,
        false,
    );

    const { taskItems$, updateTaskTime, tags } = useTaskListContext();

    const showLegacyWarning = useMemo(() => {
        const hasLegacyConnection =
            userData?.integrationsData?.hasOwnProperty("google") ||
            userData?.integrationsData?.hasOwnProperty("microsoft");
        const hasNewConnection = userData?.integrationsData?.hasOwnProperty("calendar");
        return hasLegacyConnection && !hasNewConnection;
    }, [userData?.integrationsData]);

    const renderErrors = () => {
        const elements: React.ReactNode[] = [];
        if (showLegacyWarning) {
            elements.push(
                <LifeAtToastContainer
                    toastContent={
                        <LegacyMigrationContainer>
                            <LegacyMigrationIcon>⭐</LegacyMigrationIcon>
                            <span>We've improved our calendar experience! Please sign in again.</span>
                        </LegacyMigrationContainer>
                    }
                    button={
                        <LegacyMigrationButton
                            variant="solid"
                            overrideBackgroundColor={theme.textColorPrimary}
                            overrideTextColor={theme.backgroundPrimary}
                            disableHover
                            onClick={() => settingsModalRef.current?.open()}
                        >
                            Try Now
                        </LegacyMigrationButton>
                    }
                />,
            );
        }
        if (props.errorMsg) {
            elements.push(
                <LifeAtErrorToast
                    errorMessage={props.errorMsg}
                    handleClose={() => BrowserUtil.clearCacheReloadPage()}
                    actionLabel={"Reload"}
                />,
            );
        }
        if (errorMsg) {
            elements.push(
                <LifeAtErrorToast
                    errorMessage={errorMsg}
                    handleClose={() => BrowserUtil.clearCacheReloadPage()}
                    actionLabel={"Reload"}
                />,
            );
        }

        if (elements.length === 0) {
            return null;
        }
        return <ErrorMessageContainer>{...elements}</ErrorMessageContainer>;
    };

    const dateSelectionStrategy = useMemo(
        () =>
            dateRangeStrategy(
                new Date(viewingDate.getTime() - EVENT_PAGE_SIZE_BY_DAYS.PrevPage * MILLISECONDS_PER_DAY),
                new Date(viewingDate.getTime() + EVENT_PAGE_SIZE_BY_DAYS.NextPage * MILLISECONDS_PER_DAY),
            ),
        [viewingDate],
    );
    const recurringTasks = useRecurringTasks({
        dateSelectionStrategy,
    });

    const getAllItemsForCalendar = useCallback(() => {
        const updatedCalendarItems: { [id: string]: AbstractCalendarItem } = {};

        // First, we get all task events (tasks that should be displayed on the
        // calendar, because they have the scheduleTime field).
        const tasksWithScheduleTime = getTaskEvents(taskItems$.value, tags);
        const projectDeadlines = getProjectDeadlineEvents(projectList$.value);

        // Go through source of truth tasksWithScheduleTime and skip adding task events that
        // are already in the local state taskEvents and are unchanged.
        for (const t of Object.keys(tasksWithScheduleTime)) {
            if (updatedCalendarItems[t] && updatedCalendarItems[t] === tasksWithScheduleTime[t]) {
                // Task is in our temp event list so we skip
                continue;
            }
            updatedCalendarItems[t] = tasksWithScheduleTime[t];
        }

        // Add recurring tasks.
        for (const recurringTask of recurringTasks) {
            if (recurringTask.schedulingDetails?.scheduleTime != null) {
                const calendarItem = new CalendarRecurringTask(recurringTask);
                updatedCalendarItems[calendarItem.id] = calendarItem;
            }
        }

        // Add project deadlines events
        for (const p of Object.keys(projectDeadlines)) {
            updatedCalendarItems[p] = projectDeadlines[p];
        }

        // Add the calendar events, since they represent the source of truth
        // from the backend.
        for (const e of Object.values(calendarEvents)) {
            updatedCalendarItems[e.id] = e;
        }
        return updatedCalendarItems;
    }, [calendarEvents, taskItems$.value, projectList$.value, recurringTasks]);

    const allCalendarItems: { [key: string]: AbstractCalendarItem } = useMemo(
        () => getAllItemsForCalendar(),
        [getAllItemsForCalendar],
    );

    // Map date locale string (#toLocalDateString()) to events on that date
    const calendarEventsDateMap = useMemo<Map<string, AbstractCalendarItem[]>>(() => {
        const next = new Map<string, AbstractCalendarItem[]>();
        addCalendarEventsToDateMap(Object.values(allCalendarItems), next);
        return next;
    }, [allCalendarItems]);
    const { currentEvents, upcomingEvents } = useMemo(
        () => getUpcomingEvent(calendarEventsDateMap.get(new Date().toLocaleDateString()) ?? []),
        [calendarEventsDateMap],
    );

    useEffect(() => {
        fetchEvents();
    }, [props.selectedCalendarList]);

    const displayFetchingCalendarsError = (error: Error) => {
        if (!hasSeenErrorMessage) {
            setErrorMsg(error.message);
            setHasSeenErrorMessage(true);
        }
    };

    const fetchEvents = (): void => {
        if (props.selectedCalendarList?.[0]?.calendarId) {
            // If the user switches to their primary calendar, we fetch events.
            // Otherwise, if it's a non-primary calendar, they need to be on Pro tier to
            // have the calendar fetch events.
            if (props.isOnlyPrimary || userData?.proUserInfo?.isProUser) {
                // Clear the map because the calendar was switched.
                if (databaseState === "connected") {
                    fetchEventsForDateRange(getDateTimestampDaysOnly(viewingDate))
                        .then((res: CalendarEvent[]) => {
                            setErrorMsg(null);
                            updateCalendarEventsMap(res);
                        })
                        .catch((error: Error) => {
                            displayFetchingCalendarsError(error);
                        });
                }
            }
        } else {
            // User has signed out of their calendar, so we clear the UI + scheduled notification.
            setCalendarEvents({});
            clearNotificationTimeouts();
        }
    };

    // Heartbeat to fetch events from g-cal every 5 minutes
    useEffect(() => {
        const heartbeatCalendarFetch = setInterval(() => fetchEvents(), FETCH_EVENTS_HEARTBEAT_MS);

        return () => clearInterval(heartbeatCalendarFetch);
    }, [props.selectedCalendarList, allCalendarItems, upcomingEvents]);

    // Heartbeat to countdown time left in meeting or time until next meeting
    useEffect(() => {
        if (window.todesktop && (currentEvents.length > 0 || upcomingEvents.length > 0)) {
            const timeToStart = millisecondsUntilNextWholeMinute(new Date()) - 100; // number of ms until next whole minutes minus 100ms for tolerance

            buildTrayTextForEventCountdown();
            let heartbeatEventCountdown: Timeout = null;
            // Set timeout to start the heartbeat at the next whole minute
            const countdownTimeout = setTimeout(() => {
                heartbeatEventCountdown = setInterval(() => {
                    buildTrayTextForEventCountdown();
                }, EVENT_COUNTDOWN_HEARTBEAT_MS);
            }, timeToStart);

            return () => {
                clearTimeout(countdownTimeout);
                clearInterval(heartbeatEventCountdown);
            };
        }
        return undefined;
    }, [currentEvents, upcomingEvents]);

    // Update the tray text with time left in meeting or time until next meeting
    const buildTrayTextForEventCountdown = useCallback(() => {
        const currentDate = new Date();
        let timeText: string;
        let eventTitle: string;

        let clearTray = false; // flag to indicate if tray text should be empty
        if (currentEvents.length > 0) {
            const eventEndDate = new Date(currentEvents[0].timeEnd);
            const timeLeft = calculateMinutesBetweenDates(eventEndDate, currentDate);
            if (currentDate < eventEndDate) {
                eventTitle =
                    currentEvents.length > 1
                        ? `${currentEvents.length} events`
                        : currentEvents[0].summary || UNTITLED_EVENT_TITLE;
                timeText = `- ${formatMinutesToString(timeLeft)} left`;
            } else {
                clearTray = true;
            }
        } else if (upcomingEvents.length > 0) {
            const eventStartDate = new Date(upcomingEvents[0].timeStart);
            const timeLeft = calculateMinutesBetweenDates(new Date(eventStartDate), currentDate);
            if (currentDate <= eventStartDate) {
                eventTitle =
                    upcomingEvents.length > 1
                        ? `${upcomingEvents.length} events`
                        : upcomingEvents[0].summary || UNTITLED_EVENT_TITLE;
                timeText = timeLeft === 0 ? `now` : `in ${formatMinutesToString(timeLeft)}`;
            } else {
                clearTray = true;
            }
        } else {
            clearTray = true;
        }

        if (clearTray) {
            updateTrayTitle("");
        } else {
            let trayText =
                eventTitle.length > TRAY_TEXT_LENGTH ? `${eventTitle.substring(0, TRAY_TEXT_LENGTH)}...` : eventTitle;
            trayText = trayText.split(/[\r\n\t]/g)[0]; //remove everything after new line
            updateTrayTitle(`${trayText} ${timeText}`);
        }
    }, [props.selectedCalendarList, currentEvents, upcomingEvents]);

    // Notification
    const buildNotificationForEvent = async (event: AbstractCalendarItem) => {
        if (!props.sendNotifications) {
            return;
        }

        const eventTime =
            `${new Date(event.timeStart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` +
            ` - ${new Date(event.timeEnd).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`; // Move this
        const notification = await buildNotification(event.summary || UNTITLED_EVENT_TITLE, {
            body: eventTime,
            requireInteraction: true,
        });

        // if meeting link exists, onclick will open the meeting. Else it will show the menubar window or focus the browser tab
        notification.onclick = () => {
            const upcomingCalendarEvent = event as CalendarEvent;
            let meetingLink = null;
            // NOTE: this logic is copied from the join button on event detailed view.
            //      There were type errors when abstracting it for both usages.
            // TODO: abstract this logic
            if (upcomingCalendarEvent.conferenceDetails) {
                meetingLink = upcomingCalendarEvent.conferenceDetails.conferenceLink;
            } else if (upcomingCalendarEvent.location && upcomingCalendarEvent.location.includes("https://")) {
                meetingLink = upcomingCalendarEvent.location;
            }

            if (meetingLink) {
                joinEventMeetingLink(meetingLink, history);
                amplitudeService.logEvent(new NotificationClickEvent("join_meeting"));
            } else {
                showMenubarWindow();
                window.focus();
                amplitudeService.logEvent(new NotificationClickEvent("open_window"));
            }
        };
    };

    // Set timeout for the next notification
    // When upcoming event changes, calculate the time difference, and set timeout to notify NOTIFICATION_OFFSET minutes before event
    useEffect(() => {
        let timeouts: Timeout[] = [];
        if (upcomingEvents.length > 0) {
            for (const event of upcomingEvents) {
                const now = new Date().getTime();
                const eventTimestamp = new Date(event.timeStart).getTime();

                const diff = Math.abs(eventTimestamp - now);
                const notifyTime = diff > NOTIFICATION_OFFSET ? diff - NOTIFICATION_OFFSET : 0;

                timeouts.push(setTimeout(() => buildNotificationForEvent(event), notifyTime));
            }
            notificationsTimeout.current = timeouts;

            return () => {
                clearNotificationTimeouts();
            };
        } else {
            clearNotificationTimeouts();
            return undefined;
        }
    }, [upcomingEvents]);

    const clearNotificationTimeouts = () => {
        if (notificationsTimeout.current.length > 0) {
            for (const timeout of notificationsTimeout.current) {
                clearTimeout(timeout);
            }
        }
        notificationsTimeout.current = [];
    };

    const updateCalendarEventsMap = (events: CalendarEvent[]) => {
        const eventsToAdd = {};
        for (const event of events) {
            eventsToAdd[event.id] = event;
        }
        setCalendarEvents(eventsToAdd);
    };

    const lastFetchedDay = useRef(new Date());
    // Handling with pagination as the user navigates between the selected date.
    const handleCalendarDateNavigate = useCallback(
        (newDate: Date, view: View, action: NavigateAction) => {
            setViewingDate(newDate);
            const dateStr = newDate.toLocaleDateString();
            const daysDiff = calculateNumDaysBetweenTimestamps(lastFetchedDay.current.getTime(), newDate.getTime());

            let needToFetchForWeek = false;
            // If in week view, we need to check adjacent dates to see if more
            // events should be fetched. For example, going backwards 1 week may
            // show events on the same day of the week as the current date, but
            // will not show events for the day before. So we check adjacent
            // dates to see if we should fetch more events, so that the user has
            // a more seamless experience.
            if (view === "week") {
                for (let i = 1; i <= EVENT_PAGE_SIZE_BY_DAYS.NextPage; i++) {
                    const forwardDate = new Date(newDate.getDate() + i);
                    if (!calendarEventsDateMap.has(forwardDate.toLocaleDateString())) {
                        needToFetchForWeek = true;
                        break;
                    }

                    const backwardDate = new Date(newDate.getDate() - i);
                    if (!calendarEventsDateMap.has(backwardDate.toLocaleDateString())) {
                        needToFetchForWeek = true;
                        break;
                    }
                }
            }

            // If the new date isn't being tracked in the map, we need to fetch events for it.
            if (
                (!calendarEventsDateMap.has(dateStr) || needToFetchForWeek) &&
                daysDiff !== 0 &&
                props.selectedCalendarList?.[0]?.calendarId
            ) {
                if (daysDiff >= EVENT_PAGE_SIZE_BY_DAYS.NextPage) {
                    lastFetchedDay.current = newDate;

                    fetchEventsForDateRange(getDateTimestampDaysOnly(newDate))
                        .then((res: CalendarEvent[]) => {
                            updateCalendarEventsMap(res);
                        })
                        .catch((error: Error) => {
                            displayFetchingCalendarsError(error);
                        });
                }
            }
        },
        [calendarEventsDateMap, todayDate, props.selectedCalendarList, allCalendarItems],
    );

    // Events to display on the calendar. Memoized due to react-big-calendar rendering behavior.
    const { events }: { events: Partial<CalendarItemInterface> & Event[] } = useMemo(
        () => ({
            events: Object.values(allCalendarItems).map((e: AbstractCalendarItem) => {
                const startDate = e.isAllDayEvent ? parseISODateStringForDate(e.timeStart) : new Date(e.timeStart);
                const endDate = e.isAllDayEvent ? parseISODateStringForDate(e.timeEnd) : new Date(e.timeEnd);
                return {
                    title: e.summary,
                    start: startDate,
                    end: endDate,
                    allDay: e.isAllDayEvent,
                    resource: e,
                    id: e.id,
                    ...e,
                };
            }),
        }),
        [allCalendarItems],
    );

    // When an event is selected (and highlighted) on the calendar.
    const handleViewEvent = useEvent((event: Partial<CalendarItemInterface> & Event) => {
        // Toggle visibility off if the same thing is selected again.
        if (event.id === viewingEventId) {
            setViewingEventId(null);
            return undefined;
        }
        if (
            selectedDetail$.value &&
            selectedDetail$.value.type === "singular" &&
            event.id === selectedDetail$.value.taskId
        ) {
            selectedDetail$.value = null;
            return undefined;
        }
        if (
            selectedDetail$.value &&
            selectedDetail$.value.type === "recurring" &&
            event.id === CalendarRecurringTask.calculateId(selectedDetail$.value.masterId, selectedDetail$.value.index)
        ) {
            selectedDetail$.value = null;
            return undefined;
        }

        // Toggle visibility on otherwise.
        switch (event.itemSource) {
            case "lifeat-task-deadline":
            case "lifeat-tasks": {
                selectedDetail$.value = { type: "singular", taskId: event.id };
                break;
            }
            case "lifeat-recurring-tasks": {
                const { masterId, index } = CalendarRecurringTask.parseId(event.id);
                selectedDetail$.value = {
                    type: "recurring",
                    masterId: masterId,
                    index: index,
                };
                break;
            }
            case "lifeat-project-deadline": {
                selectedProjectId$.value = event.id;
                mainContent$.value = "projects";
                break;
            }
            case "lifeat":
            case "google":
            case "outlook": {
                setViewingEventId(event.id);
                break;
            }
        }
        return undefined;
    });

    // Determines which events on the calendar are draggable (can be moved and resized).
    // Currently, only LifeAt tasks can be dragged. Calendar events from 3rd party API sources
    // are not editable because we do not propagate the update action to that 3rd party.
    const allowEventDrag = useCallback(
        (event: Partial<CalendarItemInterface> & Event) => {
            // Only tasks and lifeat items can be moved on the calendar. Will remove this once we
            // allow sending updates to 3rd party calendars.
            if (event) {
                return (event.itemSource === "lifeat" || event.itemSource === "lifeat-tasks") && !event.isAllDayEvent;
            } else {
                return false;
            }
        },
        [allCalendarItems],
    );

    // Determines which events on the calendar are resizable
    // Tasks in the all day gutter are not resizable
    const allowEventResize = useCallback(
        (event: Partial<CalendarItemInterface> & Event) => {
            if (event) {
                return (event.itemSource === "lifeat" || event.itemSource === "lifeat-tasks") && !event.isAllDayEvent;
            } else {
                return false;
            }
        },
        [allCalendarItems],
    );

    // Used for handling resizing and moving calendar events (via drag start and drop actions).
    // For moving events: fired once when an event is first "picked up" (start, end, allDay fields
    // are undefined) and then again when it is dropped (has all fields).
    const handleEventMove = useCallback(
        ({ event, start, end, isAllDay }: any) => {
            const currItem = allCalendarItems[event.id];
            if (currItem && ((start && end) || isAllDay)) {
                const duration = start && end ? calculateMinutesBetweenDates(start, end) : undefined;

                if (isAllDay) {
                    currItem.setIsAllDay(isAllDay);
                }
                if (start && end) {
                    if (currItem instanceof CalendarTask && !isValidTaskDuration(duration)) {
                        logError("Invalid task duration");
                        addToastNotification(
                            "Error updating task duration",
                            `Task duration must be between ${TASK_DURATION_MINIMUM_MINUTES} and ${TASK_DURATION_MAXIMUM_MINUTES} minutes`,
                            3000,
                        );
                        return;
                    }

                    currItem.setTimeStart(start.toISOString());
                    currItem.setTimeEnd(end.toISOString());
                }

                // Propagate changes to DB for task
                if (currItem instanceof CalendarTask && user) {
                    const task: CalendarTask = currItem;
                    task.setDurationMins(duration ?? DEFAULT_CALENDAR_TASK_DURATION_MINS);

                    // If the new duration doesn't match the duration of the
                    // task item, send a request to update.
                    if (duration != taskItems$.value[event.id].schedulingDetails.durationMins) {
                        TaskManagementService.updateTaskDuration(task.id, duration)
                            .then(() => {
                                TaskManagementService.updateTaskScheduleTime(task.id, start.toISOString()).catch(
                                    (e) => {
                                        logError(
                                            `Error updating task schedule time via calendar event. Error: ${JSON.stringify(
                                                e,
                                            )}`,
                                        );
                                    },
                                );
                            })
                            .catch((e) => {
                                logError(`Error updating task duration via calendar event Error: ${JSON.stringify(e)}`);
                            });
                    } else {
                        // Duration is the same, but scheduleTime has changed,
                        // so we send a request to update.
                        updateTaskTime(task.id, start.toISOString()).catch((e) => {
                            logError(
                                `Error updating task schedule time via calendar event. Error: ${JSON.stringify(e)}`,
                            );
                        });
                    }
                }
            }
        },
        [allCalendarItems, taskItems$.value, user],
    );

    // TODO: expose to a separate hook so we can use anywhere in the app and still
    //      update the state here instead of using the context API (setDraggedTask)
    const draggedTask$ = useComputed<Identifiable<ClientTaskItemType> | null>(() => {
        if (draggedTaskEvent$.value.type === "recurring") {
            return null;
        }
        const document = taskItems$.value[draggedTaskEvent$.value.taskId];
        if (document == null) {
            return null;
        }
        return {
            id: draggedTaskEvent$.value.taskId,
            document,
        };
    });
    useSignalEffect(() => {
        if (draggedTaskEvent$.value?.type === "singular") {
            const taskEv = CalendarTask.fromTask(
                draggedTask$.value.id,
                draggedTask$.value.document,
                new Date(),
                DEFAULT_CALENDAR_TASK_DURATION_MINS,
                getTaskTagFromTagArray(draggedTask$.value.document.tagId, tags),
            );
            setDraggedEvent(taskEv);
        }
        // NOTE: intentionally not including taskItems in the dependency array,
        //  causing lag when performing drag and drop interaction.
    });

    // DND (2): Gets the dragged item and continues to fire while the mouse is held down and dragging.
    const dragFromOutsideItem = useCallback(() => {
        return draggedEvent;
    }, [draggedEvent]);

    // DND (3): Fired when an item is dropped (mouse click released while dragging) from
    // outside of react-big-calendar.
    const onDropFromOutside = useEvent(({ start, end }) => {
        if (draggedTaskEvent$.value?.type === "recurring") {
            // TODO [LAS-1898]: implement drag and drop for recurring tasks.
            addToastNotification(RECURRING_DROP_ERROR_TITLE, RECURRING_DROP_ERROR_DESCRIPTION);
            return;
        }
        const draggedTaskId = draggedTaskEvent$.value?.type === "singular" ? draggedTaskEvent$.value.taskId : null;
        if (draggedEvent && draggedTaskId && taskItems$.value[draggedTaskId]) {
            const taskId = draggedEvent.id;
            const startDate = new Date(start);

            // Propagate changes to DB for task
            updateTaskTime(taskId, startDate.toISOString()).catch((e) => {
                logError(`Error updating task schedule time via drag and drop. Error: ${JSON.stringify(e)}`);
            });

            const previousTime =
                taskItems$.value[draggedTaskId]?.schedulingDetails?.scheduleTime ||
                taskItems$.value[draggedTaskId]?.schedulingDetails?.scheduleDate;

            amplitudeService.logEvent(
                new AddTaskToCalendarEvent({
                    source: props.amplitudeEventSource,
                    numDaysInFuture: differenceInDays(startDate, new Date()),
                    scheduledTime: startDate.toISOString(),
                    method: "drag",
                    previousTime: previousTime ?? undefined,
                }),
            );
        }
    });

    // Styles for the calendar
    const { style } = useMemo(
        () => ({
            style: { width: Styles.sidebarWidth },
        }),
        [],
    );

    const [, drop] = useDrop({
        accept: ItemTypes.TASK,
        canDrop: () => true,
        drop: () => {
            draggedTaskEvent$.value = null;
        },
        collect: (monitor) => ({
            canDrop: monitor.canDrop(),
            isOver: monitor.isOver(),
        }),
    });

    return (
        <CalendarDayViewWrapper className={props.className}>
            {allCalendarItems && allCalendarItems[viewingEventId] && (
                <StyledCalendarEventDetailView
                    className={`calendar-events-detailed-view`}
                    event={allCalendarItems[viewingEventId] as CalendarEvent}
                    isVisible={viewingEventId && !!allCalendarItems[viewingEventId]}
                    handleHide={() => setViewingEventId(null)}
                />
            )}
            {renderErrors()}

            <RbcCalendarWrapper id={"rbc-cal-wrap"} ref={drop}>
                {/* Custom props for react-big-calendar components should be passed as context. Other workarounds like HOC or useRef are too hacky. */}
                <CalendarToolbarPropsContext.Provider
                    value={{
                        calendars: props.calendars,
                        selectedCalendarList: props.selectedCalendarList,
                        onSelectedCalendarChange: props.onSelectedCalendarChange,
                        onCalendarConnectionRemove: props.onCalendarConnectionRemove,
                        settingsModalRef,
                    }}
                >
                    {/*Note: the key makes the calendar re-render when the visibility changes*/}
                    <DndCalendar
                        key={`dnd-calendar-${props.show}`}
                        components={COMPONENTS}
                        defaultView={props.calendarView ?? "day"}
                        view={props.calendarView ?? "day"}
                        events={events}
                        localizer={localizer}
                        startAccessor={"start"}
                        endAccessor={"end"}
                        scrollToTime={todayDate}
                        step={CALENDAR_EVENT_STEP}
                        timeslots={CALENDAR_STEP_TIME_SLOTS}
                        onNavigate={handleCalendarDateNavigate}
                        onSelectEvent={handleViewEvent}
                        style={style}
                        draggableAccessor={allowEventDrag}
                        resizableAccessor={allowEventResize}
                        onEventDrop={handleEventMove}
                        onEventResize={handleEventMove}
                        onDragStart={handleEventMove}
                        dragFromOutsideItem={dragFromOutsideItem}
                        onDropFromOutside={onDropFromOutside}
                    />
                </CalendarToolbarPropsContext.Provider>
            </RbcCalendarWrapper>
        </CalendarDayViewWrapper>
    );
}

function addCalendarEventsToDateMap(
    newEvents: AbstractCalendarItem[],
    dateMap: Map<string, AbstractCalendarItem[]>,
): void {
    for (const event of newEvents) {
        // For all day events, add it to the map on the day it ends
        const eventDate = event.isAllDayEvent ? new Date(event.timeEnd) : new Date(event.timeStart);
        const dateStr = eventDate.toLocaleDateString();

        if (dateMap.has(dateStr)) {
            dateMap.get(dateStr).push(event);
        } else {
            dateMap.set(dateStr, [event]);
        }
    }
}

// Get the next upcoming event
// by iterating through today's event and getting the first one at a later
// maybe we do this when we create one of the maps
const getUpcomingEvent = (todaysEvents: AbstractCalendarItem[]) => {
    const currDate = new Date();
    let currentEventsList: AbstractCalendarItem[] = [];
    let upcomingEventsList: AbstractCalendarItem[] = [];

    for (const event of todaysEvents) {
        const eventStart = new Date(event.timeStart);
        const eventEnd = new Date(event.timeEnd);

        if (isDateInRange(currDate, eventStart, eventEnd) && !event.isAllDayEvent) {
            // event is currently happening and is not an all day event
            const result = currentEventsList.filter(
                (currEvent) => currEvent.id === event.id && currEvent.timeEnd === event.timeEnd,
            );
            if (result.length === 0) {
                //event doesn't already exist in currentEvents array
                currentEventsList.push(event);
            }
        } else if (isBefore(currDate, eventStart)) {
            // event is coming up
            if (upcomingEventsList.length === 0 || isBefore(eventStart, new Date(upcomingEventsList[0].timeStart))) {
                //upcoming events doesn't exist or needs to be updated
                upcomingEventsList = [event];
            } else if (upcomingEventsList[0].timeStart === event.timeStart && upcomingEventsList[0].id !== event.id) {
                //multiple upcoming events starting at the same time
                upcomingEventsList.push(event);
            }
        }
    }

    if (upcomingEventsList.length === 0 && currentEventsList.length === 0) {
        updateTrayTitle("");
    }

    return {
        upcomingEvents: upcomingEventsList,
        currentEvents: currentEventsList,
    };
};
